// src/pages/billing/BillingReportsPage.jsx
import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import StatsCard from '../../components/dashboard/StatsCard';
import RevenueChart from '../../components/billing/RevenueChart';
import PaymentStatusChart from '../../components/billing/PaymentStatusChart';
import TopCustomersTable from '../../components/billing/TopCustomersTable';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";

// Inline AgingReport component
const AgingReport = ({ data, loading }) => {
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <p>No aging data available</p>
      </div>
    );
  }

  const totalAmount = data.reduce((sum, item) => sum + item.amount, 0);

  return (
    <div className="space-y-4">
      {data.map((item, index) => (
        <div key={index} className="flex items-center justify-between">
          <div className="flex items-center flex-1">
            <div className={`w-4 h-4 rounded ${item.color} mr-3`}></div>
            <span className="text-sm text-gray-700">{item.label}</span>
          </div>
          <div className="text-right">
            <div className="text-sm font-medium text-gray-900">
              {formatCurrency(item.amount)}
            </div>
            <div className="text-xs text-gray-500">
              {totalAmount > 0 ? ((item.amount / totalAmount) * 100).toFixed(1) : 0}%
            </div>
          </div>
        </div>
      ))}
      
      <div className="border-t pt-4">
        <div className="flex justify-between">
          <span className="text-sm font-semibold text-gray-900">Total Outstanding:</span>
          <span className="text-sm font-semibold text-gray-900">
            {formatCurrency(totalAmount)}
          </span>
        </div>
      </div>
    </div>
  );
};

// Inline MonthlySummaryTable component
const MonthlySummaryTable = ({ data, loading }) => {
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <p>No data available for the selected period</p>
      </div>
    );
  }

  return (
    <table className="min-w-full divide-y divide-gray-200">
      <thead className="bg-gray-50">
        <tr>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Period
          </th>
          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
            Sales Revenue
          </th>
          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
            Purchase Amount
          </th>
          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
            Quotation Value
          </th>
          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
            Sales Invoices
          </th>
          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
            GST Collected
          </th>
        </tr>
      </thead>
      <tbody className="bg-white divide-y divide-gray-200">
        {data.map((item, index) => (
          <tr key={index} className="hover:bg-gray-50">
            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
              {item.period}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-green-600">
              {formatCurrency(item.salesRevenue)}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-orange-600">
              {formatCurrency(item.purchaseAmount)}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-purple-600">
              {formatCurrency(item.quotationValue)}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">
              {item.salesCount}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-blue-600">
              {formatCurrency(item.gstCollected)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const BillingReportsPage = () => {
  const { vendorProfile } = useAuth();
  const { showAlert } = useNotification();
  
  const [dateRange, setDateRange] = useState([
    new Date(new Date().getFullYear(), new Date().getMonth(), 1), // First day of current month
    new Date() // Today
  ]);
  
  const [stats, setStats] = useState(null);
  const [revenueData, setRevenueData] = useState([]);
  const [topCustomers, setTopCustomers] = useState([]);
  const [agingData, setAgingData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reportType, setReportType] = useState('monthly');

  // Load all billing data
  const loadReportsData = async () => {
    if (!vendorProfile?.uid) return;

    try {
      setLoading(true);
      const [startDate, endDate] = dateRange;
      
      // Load sales invoices - simplified query to avoid index requirement
      const salesQuery = query(
        collection(db, 'invoices'),
        where('vendorId', '==', vendorProfile.uid)
      );
      const salesSnapshot = await getDocs(salesQuery);
      let salesInvoices = salesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      // Filter by date range in JavaScript to avoid composite index
      salesInvoices = salesInvoices.filter(invoice => {
        const invoiceDate = invoice.createdAt?.toDate ? invoice.createdAt.toDate() : new Date(invoice.createdAt);
        return invoiceDate >= startDate && invoiceDate <= endDate;
      }).sort((a, b) => {
        const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
        const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
        return dateB - dateA;
      });

      // Load purchase invoices - simplified query
      const purchaseQuery = query(
        collection(db, 'purchaseInvoices'),
        where('vendorId', '==', vendorProfile.uid)
      );
      const purchaseSnapshot = await getDocs(purchaseQuery);
      let purchaseInvoices = purchaseSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      // Filter by date range in JavaScript
      purchaseInvoices = purchaseInvoices.filter(invoice => {
        const invoiceDate = invoice.createdAt?.toDate ? invoice.createdAt.toDate() : new Date(invoice.createdAt);
        return invoiceDate >= startDate && invoiceDate <= endDate;
      }).sort((a, b) => {
        const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
        const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
        return dateB - dateA;
      });

      // Load quotations - simplified query
      const quotationQuery = query(
        collection(db, 'quotations'),
        where('vendorId', '==', vendorProfile.uid)
      );
      const quotationSnapshot = await getDocs(quotationQuery);
      let quotations = quotationSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      // Filter by date range in JavaScript
      quotations = quotations.filter(quotation => {
        const quotationDate = quotation.createdAt?.toDate ? quotation.createdAt.toDate() : new Date(quotation.createdAt);
        return quotationDate >= startDate && quotationDate <= endDate;
      }).sort((a, b) => {
        const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
        const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
        return dateB - dateA;
      });

      // Calculate stats
      const calculatedStats = calculateOverallStats(salesInvoices, purchaseInvoices, quotations);
      setStats(calculatedStats);

      // Generate revenue data for charts
      const revenueChartData = generateRevenueData(salesInvoices, purchaseInvoices, quotations, reportType);
      setRevenueData(revenueChartData);

      // Generate top customers
      const customersData = generateTopCustomers(salesInvoices);
      setTopCustomers(customersData);

      // Generate aging data (for receivables)
      const aging = generateAgingData(salesInvoices);
      setAgingData(aging);

    } catch (error) {
      console.error('Error loading reports data:', error);
      showAlert('Failed to load reports data', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Calculate overall statistics
  const calculateOverallStats = (salesInvoices, purchaseInvoices, quotations) => {
    // Sales statistics
    const totalSalesAmount = salesInvoices.reduce((sum, inv) => sum + (inv.totalAmount || 0), 0);
    const totalSalesGST = salesInvoices.reduce((sum, inv) => sum + (inv.totalTaxAmount || 0), 0);
    const salesCount = salesInvoices.length;

    // Purchase statistics
    const totalPurchaseAmount = purchaseInvoices.reduce((sum, inv) => sum + (inv.totalAmount || 0), 0);
    const totalPurchaseGST = purchaseInvoices.reduce((sum, inv) => sum + (inv.totalGstAmount || 0), 0);
    const purchaseCount = purchaseInvoices.length;

    // Quotation statistics
    const totalQuotationValue = quotations.reduce((sum, quo) => sum + (quo.totalAmount || 0), 0);
    const quotationCount = quotations.length;
    const activeQuotations = quotations.filter(q => q.status === 'active').length;

    // Payment status for chart
    const statusBreakdown = {
      paid: salesInvoices.filter(inv => inv.status === 'paid').length,
      pending: salesInvoices.filter(inv => inv.status === 'pending' || !inv.status).length,
      overdue: salesInvoices.filter(inv => inv.status === 'overdue').length,
      draft: salesInvoices.filter(inv => inv.status === 'draft').length,
      cancelled: salesInvoices.filter(inv => inv.status === 'cancelled').length
    };

    return {
      // Sales
      totalSalesAmount,
      totalSalesGST,
      salesCount,
      
      // Purchases
      totalPurchaseAmount,
      totalPurchaseGST,
      purchaseCount,
      
      // Quotations
      totalQuotationValue,
      quotationCount,
      activeQuotations,
      
      // Profit calculation
      grossProfit: totalSalesAmount - totalPurchaseAmount,
      
      // For payment status chart
      statusBreakdown
    };
  };

  // Generate revenue data for charts
  const generateRevenueData = (salesInvoices, purchaseInvoices, quotations, type) => {
    const data = [];
    const now = new Date();
    let periods = [];

    // Generate periods based on report type
    if (type === 'daily') {
      for (let i = 6; i >= 0; i--) {
        const date = new Date(now);
        date.setDate(date.getDate() - i);
        periods.push({
          period: date.toLocaleDateString('en-IN', { month: 'short', day: 'numeric' }),
          start: new Date(date.getFullYear(), date.getMonth(), date.getDate()),
          end: new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1)
        });
      }
    } else if (type === 'weekly') {
      for (let i = 7; i >= 0; i--) {
        const date = new Date(now);
        date.setDate(date.getDate() - (i * 7));
        const weekStart = new Date(date);
        weekStart.setDate(date.getDate() - date.getDay());
        periods.push({
          period: `Week ${Math.ceil(date.getDate() / 7)}`,
          start: weekStart,
          end: new Date(weekStart.getTime() + 7 * 24 * 60 * 60 * 1000)
        });
      }
    } else if (type === 'monthly') {
      for (let i = 11; i >= 0; i--) {
        const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
        periods.push({
          period: date.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' }),
          start: new Date(date.getFullYear(), date.getMonth(), 1),
          end: new Date(date.getFullYear(), date.getMonth() + 1, 0)
        });
      }
    } else if (type === 'quarterly') {
      for (let i = 3; i >= 0; i--) {
        const quarterStart = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3 - (i * 3), 1);
        const quarterEnd = new Date(quarterStart.getFullYear(), quarterStart.getMonth() + 3, 0);
        periods.push({
          period: `Q${Math.floor(quarterStart.getMonth() / 3) + 1} ${quarterStart.getFullYear()}`,
          start: quarterStart,
          end: quarterEnd
        });
      }
    }

    // Calculate data for each period
    periods.forEach(period => {
      const periodSales = salesInvoices.filter(inv => {
        const invDate = inv.createdAt?.toDate ? inv.createdAt.toDate() : new Date(inv.createdAt);
        return invDate >= period.start && invDate <= period.end;
      });

      const periodPurchases = purchaseInvoices.filter(inv => {
        const invDate = inv.createdAt?.toDate ? inv.createdAt.toDate() : new Date(inv.createdAt);
        return invDate >= period.start && invDate <= period.end;
      });

      const periodQuotations = quotations.filter(quo => {
        const quoDate = quo.createdAt?.toDate ? quo.createdAt.toDate() : new Date(quo.createdAt);
        return quoDate >= period.start && quoDate <= period.end;
      });

      const salesRevenue = periodSales.reduce((sum, inv) => sum + (inv.totalAmount || 0), 0);
      const purchaseAmount = periodPurchases.reduce((sum, inv) => sum + (inv.totalAmount || 0), 0);
      const quotationValue = periodQuotations.reduce((sum, quo) => sum + (quo.totalAmount || 0), 0);
      const gstCollected = periodSales.reduce((sum, inv) => sum + (inv.totalTaxAmount || 0), 0);

      data.push({
        period: period.period,
        salesRevenue,
        purchaseAmount,
        quotationValue,
        salesCount: periodSales.length,
        gstCollected,
        revenue: salesRevenue, // For chart compatibility
        paid: salesRevenue // Assuming all are paid for chart
      });
    });

    return data;
  };

  // Generate top customers data
  const generateTopCustomers = (salesInvoices) => {
    const customerMap = {};

    salesInvoices.forEach(invoice => {
      const customerName = invoice.customerName || 'Unknown Customer';
      if (!customerMap[customerName]) {
        customerMap[customerName] = {
          name: customerName,
          email: invoice.customerEmail || '',
          totalAmount: 0,
          paidAmount: 0,
          invoiceCount: 0,
          lastInvoiceDate: null
        };
      }

      customerMap[customerName].totalAmount += invoice.totalAmount || 0;
      customerMap[customerName].paidAmount += invoice.totalAmount || 0; // Assuming paid
      customerMap[customerName].invoiceCount += 1;
      
      const invoiceDate = invoice.createdAt?.toDate ? invoice.createdAt.toDate() : new Date(invoice.createdAt);
      if (!customerMap[customerName].lastInvoiceDate || invoiceDate > customerMap[customerName].lastInvoiceDate) {
        customerMap[customerName].lastInvoiceDate = invoiceDate;
      }
    });

    return Object.values(customerMap)
      .sort((a, b) => b.totalAmount - a.totalAmount)
      .slice(0, 10);
  };

  // Generate aging data for receivables
  const generateAgingData = (salesInvoices) => {
    const now = new Date();
    const aging = {
      current: 0,     // 0-30 days
      thirty: 0,      // 31-60 days
      sixty: 0,       // 61-90 days
      ninety: 0       // 90+ days
    };

    salesInvoices.forEach(invoice => {
      if (invoice.status === 'pending') {
        const invoiceDate = invoice.createdAt?.toDate ? invoice.createdAt.toDate() : new Date(invoice.createdAt);
        const daysDiff = Math.floor((now - invoiceDate) / (1000 * 60 * 60 * 24));
        const amount = invoice.totalAmount || 0;

        if (daysDiff <= 30) {
          aging.current += amount;
        } else if (daysDiff <= 60) {
          aging.thirty += amount;
        } else if (daysDiff <= 90) {
          aging.sixty += amount;
        } else {
          aging.ninety += amount;
        }
      }
    });

    return [
      { label: 'Current (0-30 days)', amount: aging.current, color: 'bg-green-500' },
      { label: '31-60 days', amount: aging.thirty, color: 'bg-yellow-500' },
      { label: '61-90 days', amount: aging.sixty, color: 'bg-orange-500' },
      { label: '90+ days', amount: aging.ninety, color: 'bg-red-500' }
    ];
  };

  useEffect(() => {
    if (vendorProfile?.uid) {
      loadReportsData();
    }
  }, [dateRange, reportType, vendorProfile?.uid]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const exportReport = (format) => {
    console.log(`Exporting report in ${format} format`);
    showAlert(`Export to ${format.toUpperCase()} feature will be implemented soon!`, 'info');
  };

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="py-4 flex justify-between items-center border-b border-gray-200">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Billing Reports</h1>
            <p className="mt-1 text-sm text-gray-600">
              Analyze your billing performance and revenue trends
            </p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={() => exportReport('pdf')}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
              Export PDF
            </button>
            <button
              onClick={() => exportReport('excel')}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Export Excel
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white shadow rounded-md p-4 my-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <label className="block text-sm font-medium text-gray-700">Date Range</label>
              <DatePicker
                selectsRange={true}
                startDate={dateRange[0]}
                endDate={dateRange[1]}
                onChange={(update) => setDateRange(update)}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Report Type</label>
              <select
                value={reportType}
                onChange={(e) => setReportType(e.target.value)}
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm rounded-md"
              >
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
              </select>
            </div>
            <div className="flex items-end">
              <button
                onClick={loadReportsData}
                className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-orange-600 hover:bg-orange-700"
              >
                <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Stats Overview */}
        {stats && (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-6 mb-6">
            <StatsCard
              title="Sales Revenue"
              value={formatCurrency(stats.totalSalesAmount)}
              icon={
                <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              }
            />
            <StatsCard
              title="Purchase Amount"
              value={formatCurrency(stats.totalPurchaseAmount)}
              icon={
                <svg className="h-6 w-6 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                </svg>
              }
            />
            <StatsCard
              title="Gross Profit"
              value={formatCurrency(stats.grossProfit)}
              icon={
                <svg className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              }
              change={stats.grossProfit > 0 ? 'positive' : 'negative'}
            />
            <StatsCard
              title="Quotation Value"
              value={formatCurrency(stats.totalQuotationValue)}
              icon={
                <svg className="h-6 w-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              }
            />
            <StatsCard
              title="GST Collected"
              value={formatCurrency(stats.totalSalesGST)}
              icon={
                <svg className="h-6 w-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
              }
            />
            <StatsCard
              title="GST Paid"
              value={formatCurrency(stats.totalPurchaseGST)}
              icon={
                <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v2a2 2 0 002 2z" />
                </svg>
              }
            />
          </div>
        )}

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Revenue Trend Chart */}
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Revenue Trend</h3>
              <div className="h-80">
                <RevenueChart data={revenueData} loading={loading} />
              </div>
            </div>
          </div>

          {/* Payment Status Distribution */}
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Payment Status Distribution</h3>
              <div className="h-80">
                <PaymentStatusChart data={stats?.statusBreakdown} loading={loading} />
              </div>
            </div>
          </div>
        </div>

        {/* Detailed Reports Tables */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Top Customers */}
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Top Customers by Revenue</h3>
              <p className="mt-1 text-sm text-gray-500">Customers generating the most revenue</p>
            </div>
            <div className="px-4 py-3 sm:px-6">
              <TopCustomersTable customers={topCustomers} loading={loading} />
            </div>
          </div>

          {/* Aging Report */}
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Accounts Receivable Aging</h3>
              <p className="mt-1 text-sm text-gray-500">Outstanding invoices by age</p>
            </div>
            <div className="px-4 py-3 sm:px-6">
              <AgingReport data={agingData} loading={loading} />
            </div>
          </div>
        </div>

        {/* Monthly Summary Table */}
        <div className="mt-6 bg-white overflow-hidden shadow rounded-lg">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Revenue Summary</h3>
            <p className="mt-1 text-sm text-gray-500">Detailed breakdown of revenue and transactions by period</p>
          </div>
          <div className="overflow-x-auto">
            <MonthlySummaryTable data={revenueData} loading={loading} />
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default BillingReportsPage;